import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as colors from "common/colors";
import * as numbers from "common/numbers";
import { isCoverageSplit } from "domain/towerResolvers";
import {
  boxHeightPx,
  buildBand,
  buildRetentionEntries,
  buildTooltipLines,
} from "domain/towerRetentionBand";
import * as staticDataService from "services/staticDataService";
import * as utils from "utils";

const DEFAULT_START_COLOR = "#b3d4ff";
const DEFAULT_END_COLOR = "#b5f6c8";
const DEFAULT_LIST_COLORS = [
  "#b3d4ff",
  "#c5f3d9",
  "#ffe3ba",
  "#c8f7e2",
  "#ffd6e8",
  "#dce5d2",
  "#f5f0c3",
  "#c8e6c9",
];
const COLOR_MODE = {
  SCALE: "SCALE",
  LIST: "LIST",
  RANDOM: "RANDOM",
};
const MIN_LAYER_PX = 72;
const BASE_CANVAS_HEIGHT = 560;
const MAX_CANVAS_HEIGHT = 900;
const MIN_TEXT_PX = 56;
const MIN_TICK_GAP_PX = 18;
const LIGHTEN_FRACTION = 0.25;

const resolveShareLimit = (share, layerLimit) => {
  if (share?.shareOfLimit != null) {
    return share.shareOfLimit;
  }
  if (share?.share != null && layerLimit != null) {
    return (share.share / 100) * layerLimit;
  }
  return layerLimit ?? 0;
};

const formatShareDescription = (share, layer) => {
  const shareLimit = resolveShareLimit(share, layer.limit);
  const abbreviatedLimit = numbers.abbreviated(shareLimit);
  const layerLabel = getLayerLabel(layer);

  if (
    layer.limit != null &&
    shareLimit != null &&
    !numbers.approxEqual(shareLimit, layer.limit)
  ) {
    return `${abbreviatedLimit} part of ${layerLabel}`;
  }

  return layerLabel;
};

const getLayerLabel = (layer) => {
  const isLead = layer?.attachment === 0;
  const limitLabel = Number.isFinite(layer?.limit)
    ? numbers.abbreviated(layer.limit)
    : "–";

  const base = isLead ? `Lead $${limitLabel}` : utils.shortLayerName(layer);
  return base;
};

const createTicks = (layers, maxHeight) => {
  const boundaries = new Set();

  layers.forEach((layer) => {
    const attachment = layer?.attachment ?? 0;
    const top = attachment + (layer?.limit ?? 0);
    if (Number.isFinite(attachment)) boundaries.add(attachment);
    if (Number.isFinite(top)) boundaries.add(top);
  });

  if (boundaries.size > 0) {
    const ticks = [...boundaries].sort((a, b) => a - b);
    if (!ticks.includes(0)) {
      ticks.unshift(0);
    }
    if (
      Number.isFinite(maxHeight) &&
      !numbers.approxEqual(ticks[ticks.length - 1], maxHeight)
    ) {
      ticks.push(maxHeight);
    }
    return ticks;
  }

  if (maxHeight <= 0) {
    return [0];
  }

  const desiredTicks = 6;
  const rawStep = maxHeight / desiredTicks;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep || 1));
  const niceStep = Math.ceil(rawStep / magnitude) * magnitude;

  const ticks = [];
  for (let value = 0; value <= maxHeight + 1e-6; value += niceStep) {
    ticks.push(Math.min(value, maxHeight));
  }

  if (ticks[ticks.length - 1] !== maxHeight) {
    ticks.push(maxHeight);
  }

  return ticks;
};

const buildSharePalette = (layerColor, count) => {
  if (count <= 1) {
    return [layerColor];
  }

  if (colors.toRgb(layerColor) != null) {
    return colors
      .shades(
        layerColor,
        colors.lightenHex(layerColor, LIGHTEN_FRACTION),
        count + 1
      )
      .slice(1);
  }

  const hsl = colors.parseHsl(layerColor);
  if (!hsl) {
    return Array.from({ length: count }).map(() => layerColor);
  }

  const startL = numbers.clamp(hsl.l, 30, 80);
  const endL = numbers.clamp(startL + 18, 40, 92);
  const step = (endL - startL) / (count + 1);

  return Array.from({ length: count }).map((_, index) => {
    const lightness = startL + step * (index + 1);
    return `hsl(${hsl.h}, ${hsl.s}%, ${lightness}%)`;
  });
};

const buildPalette = (count, { mode, startColor, endColor, listColors }) => {
  if (!Number.isFinite(count) || count <= 0) {
    return [];
  }

  const safeStart =
    colors.toRgb(startColor) != null ? startColor : DEFAULT_START_COLOR;
  const safeEnd = colors.toRgb(endColor) != null ? endColor : DEFAULT_END_COLOR;

  if (mode === COLOR_MODE.LIST) {
    const palette = (listColors || []).filter((c) => colors.toRgb(c) != null);
    if (palette.length > 0) {
      return Array.from({ length: count }).map(
        (_, idx) => palette[idx % palette.length]
      );
    }
  }
  return colors.shades(safeStart, safeEnd, count);
};

const buildRandomLayerColor = (layer) => {
  const carrierSeed =
    layer?.carrier ||
    (layer?.shares || [])
      .map((share) => share?.carrier)
      .filter((value) => value)
      .join(" / ") ||
    "";
  const seed = `${carrierSeed}-${layer?.limit ?? ""}-${
    layer?.attachment ?? ""
  }`;
  const hue = (colors.hashToHue(seed) * 137.508) % 360;
  return `hsl(${hue}, 65%, 68%)`;
};

const buildShareRows = (layers) => {
  return layers.map((layer, index) => {
    const layerLabel = getLayerLabel(layer);
    const shares =
      layer?.shares?.length > 0
        ? layer.shares
        : [
            {
              carrier: layer?.carrier ?? null,
              shareOfLimit: layer?.limit ?? 0,
              shareOfGrossPremium: layer?.grossPremium ?? null,
              comments: layer?.comments ?? null,
              reference: layer?.reference ?? null,
            },
          ];

    const carrierNames = shares
      .map((share) => share.carrier || "–")
      .join(" / ");

    const premiumFromShares = shares
      .map((share) => share.shareOfGrossPremium ?? share.grossPremium ?? null)
      .filter((v) => Number.isFinite(v))
      .reduce((a, b) => a + b, 0);

    const premium =
      Number.isFinite(layer?.grossPremium) && layer.grossPremium > 0
        ? layer.grossPremium
        : premiumFromShares || null;

    const notes = shares
      .map((share) => share.comments || share.reference || "")
      .filter((v) => v)
      .join(" / ");

    return {
      key: `${layerLabel}-${index}`,
      carrier: carrierNames,
      layerLabel,
      premium,
      notes,
    };
  });
};

const formatPremium = (value) => {
  if (value == null || Number.isNaN(value)) {
    return "-";
  }
  return `$${numbers.abbreviated(value)}`;
};

const RetentionBox = ({ entry, largestValue, sx }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      minWidth: 0,
      ...sx,
    }}
  >
    <Box
      sx={{
        width: "100%",
        height: `${boxHeightPx(entry.value, largestValue)}px`,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        background: "transparent",
        boxSizing: "border-box",
        borderRadius: "6px",
      }}
    />
    <Typography
      variant={"caption"}
      sx={{ fontWeight: 600, color: "text.secondary", textAlign: "center" }}
    >
      {`${entry.label} $${numbers.abbreviated(entry.value)}`}
    </Typography>
  </Box>
);

const RetentionBand = ({ layers, entries }) => {
  const { columns, unmatched } = buildBand(layers, entries);
  const largestValue = Math.max(...entries.map((entry) => entry.value));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        marginLeft: "3.5rem",
        gap: "0.5rem",
      }}
    >
      {columns.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          {columns.map((column, columnIndex) => (
            <Box
              key={`SIR_COLUMN_${columnIndex}`}
              data-testid={"tower-retention-column"}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                width: `${column.widthPct}%`,
                boxSizing: "border-box",
              }}
            >
              {column.entries.map((entry) => (
                <RetentionBox
                  key={`SIR_${entry.id}`}
                  entry={entry}
                  largestValue={largestValue}
                  sx={{ flex: "1 1 0" }}
                />
              ))}
            </Box>
          ))}
        </Box>
      )}
      {unmatched.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
          {unmatched.map((entry) => (
            <RetentionBox
              key={`SIR_${entry.id}`}
              entry={entry}
              largestValue={largestValue}
              sx={{ flex: "1 1 0" }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const TowerDiagram = ({
  tower,
  minLayerPx = MIN_LAYER_PX,
  baseCanvasHeight = BASE_CANVAS_HEIGHT,
  paletteConfig,
  showSir = false,
  retentions,
  retentionMaps,
}) => {
  const layers = (tower ?? [])
    .filter((layer) => layer?.limit != null)
    .sort((a, b) => (a.attachment ?? 0) - (b.attachment ?? 0));

  if (layers.length === 0) {
    return null;
  }

  const maxHeight = Math.max(
    ...layers.map((layer) => (layer.attachment ?? 0) + (layer.limit ?? 0)),
    0
  );

  const basePxPerUnit = baseCanvasHeight / (maxHeight || 1);
  const maxPxPerUnit = MAX_CANVAS_HEIGHT / (maxHeight || 1);
  const pxPerUnit = Math.min(basePxPerUnit, maxPxPerUnit);

  const layerBaseMetrics = layers.map((layer) => {
    const layerBottom = layer?.attachment ?? 0;
    const layerHeight = layer?.limit ?? 0;
    const baseHeightPx = layerHeight * pxPerUnit;
    const displayHeightPx = Math.max(baseHeightPx, minLayerPx);
    const extraHeightPx = displayHeightPx - baseHeightPx;

    return {
      layer,
      layerBottom,
      layerHeight,
      displayHeightPx,
      extraHeightPx,
    };
  });

  const getSegmentExtraOffset = (
    value,
    segmentBottom,
    segmentHeight,
    extraPx
  ) => {
    if (value <= segmentBottom) {
      return 0;
    }
    if (segmentHeight <= 0) {
      return extraPx;
    }

    const fraction = Math.min(1, (value - segmentBottom) / segmentHeight);
    return extraPx * fraction;
  };

  const getExtraOffset = (value) =>
    layerBaseMetrics.reduce(
      (sum, metric) =>
        sum +
        getSegmentExtraOffset(
          value,
          metric.layerBottom,
          metric.layerHeight,
          metric.extraHeightPx
        ),
      0
    );

  const valueToPixel = (value) => value * pxPerUnit + getExtraOffset(value);

  const layerMetrics = layerBaseMetrics.map((metric) => ({
    ...metric,
    bottomPx: valueToPixel(metric.layerBottom),
  }));

  const canvasHeight = valueToPixel(maxHeight || 1);

  const ticks = createTicks(layers, maxHeight);
  const rawTickPositionsPx = ticks.map((value) => valueToPixel(value));
  const adjustedTickPositionsPx = rawTickPositionsPx.map((pos) => pos);

  for (let i = 1; i < adjustedTickPositionsPx.length; i++) {
    const prior = adjustedTickPositionsPx[i - 1];
    const minAllowed = prior + MIN_TICK_GAP_PX;
    if (adjustedTickPositionsPx[i] < minAllowed) {
      adjustedTickPositionsPx[i] = minAllowed;
    }
  }

  const overflowPx =
    adjustedTickPositionsPx.length > 0
      ? adjustedTickPositionsPx[adjustedTickPositionsPx.length - 1] -
        canvasHeight
      : 0;

  if (overflowPx > 0) {
    for (let i = 0; i < adjustedTickPositionsPx.length; i++) {
      adjustedTickPositionsPx[i] = Math.max(
        0,
        adjustedTickPositionsPx[i] - overflowPx
      );
    }
  }

  const tickPositionByValue = new Map(
    ticks.map((value, index) => [value, adjustedTickPositionsPx[index]])
  );
  const layerPalette = buildPalette(layers.length || 1, paletteConfig);
  const entries = showSir
    ? buildRetentionEntries(retentions, retentionMaps)
    : [];

  const frameSx = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "52rem",
    marginTop: "0.75rem",
    marginBottom: "3rem",
    marginLeft: "1rem",
  };

  const rowSx = {
    display: "flex",
    gap: "0px",
    height: `${canvasHeight}px`,
  };

  const axisSx = {
    position: "relative",
    width: "2.5rem",
    marginRight: "1rem",
    justifySelf: "flex-end",
    height: `${canvasHeight}px`,
  };

  const canvasSx = {
    position: "relative",
    flex: 1,
    border: "none",
    borderRadius: "0.625rem",
    background: "#fff",
    overflow: "hidden",
    height: `${canvasHeight}px`,
  };

  return (
    <Box sx={frameSx}>
      <Box sx={rowSx}>
        <Box sx={axisSx}>
          {ticks.map((value) => {
            return (
              <Box
                key={`tick-${value}`}
                sx={{
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "0.25rem",
                  width: "100%",
                  transform: "translateY(50%)",
                  bottom: `${
                    tickPositionByValue.get(value) ?? valueToPixel(value)
                  }px`,
                }}
              >
                <Typography variant={"caption"}>
                  {numbers.abbreviated(value)}
                </Typography>
                <Typography
                  variant={"caption"}
                  sx={{
                    fontSize: "20px",
                    position: "relative",
                    color: "text.secondary",
                    bottom: "2px",
                  }}
                >
                  {"▸"}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box sx={canvasSx}>
          {layerMetrics.map((metric, index) => {
            const { layer, displayHeightPx, bottomPx } = metric;
            const shares =
              layer?.shares?.length > 0
                ? layer.shares
                : [
                    {
                      carrier: layer?.carrier ?? null,
                      shareOfLimit: layer?.limit ?? 0,
                    },
                  ];
            const layerColor =
              paletteConfig.mode === COLOR_MODE.RANDOM
                ? buildRandomLayerColor(layer)
                : layerPalette[index % layerPalette.length] ??
                  paletteConfig.startColor ??
                  DEFAULT_START_COLOR;
            const sharePalette = buildSharePalette(layerColor, shares.length);

            return (
              <Box
                key={`TOWER_${index}`}
                sx={{
                  position: "absolute",
                  left: 0,
                  width: "100%",
                  display: "flex",
                  alignItems: "stretch",
                  borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                  bottom: `${bottomPx}px`,
                  height: `${displayHeightPx}px`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  {shares.map((share, shareIndex) => {
                    const shareLimit = resolveShareLimit(share, layer.limit);
                    const widthPct =
                      showSir && isCoverageSplit(layer.shares)
                        ? 100 / shares.length
                        : layer.limit != null &&
                          layer.limit !== 0 &&
                          shareLimit != null
                        ? (100 * shareLimit) / layer.limit
                        : 100;
                    const color =
                      sharePalette[shareIndex % sharePalette.length] ??
                      layerColor;
                    const shareHeightPx = displayHeightPx;
                    const showTooltip =
                      shareHeightPx < MIN_TEXT_PX || widthPct < 10;
                    const retentionLines = buildTooltipLines(layer, entries);
                    const tooltipLines = [
                      ...(showTooltip || retentionLines.length > 0
                        ? [
                            `${share.carrier ?? "–"} • ${formatShareDescription(
                              share,
                              layer
                            )}`,
                          ]
                        : []),
                      ...retentionLines,
                    ];
                    const tooltipTitle = tooltipLines.map((line, lineIndex) => (
                      <Box key={`TOOLTIP_LINE_${lineIndex}`}>{line}</Box>
                    ));

                    const content = (
                      <Box
                        key={`TOWER_${index}_${shareIndex}`}
                        data-testid={"tower-share"}
                        sx={{
                          position: "relative",
                          width: `${widthPct}%`,
                          boxSizing: "border-box",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background: color,
                            borderLeft:
                              shareIndex === 0
                                ? "none"
                                : "1px solid rgba(0, 0, 0, 0.16)",
                            zIndex: 0,
                          }}
                        />
                        <Box
                          sx={{
                            position: "relative",
                            zIndex: 2,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            padding: "0.75rem",
                            color: "#1f1f1f",
                            gap: showTooltip ? 0 : "0.25rem",
                            height: "100%",
                            boxSizing: "border-box",
                          }}
                        >
                          {showTooltip ? (
                            <Typography
                              variant={"subtitle2"}
                              sx={{ fontWeight: 700, lineHeight: 1 }}
                            >
                              {"..."}
                            </Typography>
                          ) : (
                            <>
                              <Typography
                                variant={"subtitle2"}
                                sx={{ fontWeight: 600 }}
                              >
                                {share.carrier ?? "–"}
                              </Typography>
                              <Typography variant={"body2"}>
                                {formatShareDescription(share, layer)}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    );

                    return tooltipLines.length > 0 ? (
                      <Tooltip
                        key={`TOWER_${index}_${shareIndex}`}
                        title={tooltipTitle}
                        followCursor
                      >
                        {content}
                      </Tooltip>
                    ) : (
                      content
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {showSir && entries.length > 0 && (
        <RetentionBand layers={layers} entries={entries} />
      )}
    </Box>
  );
};

const TowerTable = ({ showTable = false, shareRows }) => {
  if (!showTable || !shareRows?.length) {
    return null;
  }

  return (
    <TableContainer sx={{ marginTop: "1rem" }}>
      <Table size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell width={"30%"}>{"Carrier"}</TableCell>
            <TableCell width={"20%"}>{"Layer"}</TableCell>
            <TableCell width={"10%"} align={"right"}>
              {"Premium"}
            </TableCell>
            <TableCell width={"40%"}>{"Notes"}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...shareRows].reverse().map((row) => (
            <TableRow key={row.key}>
              <TableCell>{row.carrier}</TableCell>
              <TableCell>{row.layerLabel}</TableCell>
              <TableCell align={"right"}>
                {formatPremium(row.premium)}
              </TableCell>
              <TableCell>{row.notes || "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ScaledTowerDiagram = ({
  tower,
  minLayerPx = MIN_LAYER_PX,
  baseCanvasHeight = BASE_CANVAS_HEIGHT,
  showTable = false,
  showSir = false,
  retentions,
  retentionMaps,
}) => {
  const { data: uiCustomisation } = staticDataService.useConfigQuery(
    "ui_customisation"
  );

  const startColorConfig = uiCustomisation?.towerDiagram?.startColor;
  const endColorConfig = uiCustomisation?.towerDiagram?.endColor;
  const listColorsConfig = uiCustomisation?.towerDiagram?.listColors;
  const modeConfig = uiCustomisation?.towerDiagram?.mode;

  const paletteConfig = {
    mode:
      modeConfig === COLOR_MODE.LIST || modeConfig === COLOR_MODE.RANDOM
        ? modeConfig
        : COLOR_MODE.SCALE,
    startColor:
      colors.toRgb(startColorConfig) != null
        ? startColorConfig
        : DEFAULT_START_COLOR,
    endColor:
      colors.toRgb(endColorConfig) != null ? endColorConfig : DEFAULT_END_COLOR,
    listColors: Array.isArray(listColorsConfig)
      ? listColorsConfig
      : DEFAULT_LIST_COLORS,
  };

  const layers = (tower ?? [])
    .filter((layer) => layer?.limit != null)
    .sort((a, b) => (a.attachment ?? 0) - (b.attachment ?? 0));

  if (layers.length === 0) {
    return null;
  }

  const shareRows = buildShareRows(layers);

  return (
    <Box>
      <TowerDiagram
        tower={tower}
        minLayerPx={minLayerPx}
        baseCanvasHeight={baseCanvasHeight}
        paletteConfig={paletteConfig}
        showSir={showSir}
        retentions={retentions}
        retentionMaps={retentionMaps}
      />
      <TowerTable showTable={showTable} shareRows={shareRows} />
    </Box>
  );
};

export default ScaledTowerDiagram;
